import ConversationsApiService from '@/apis/conversationsApiService'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import useConversationActions from '@/hooks/useConversationActions'
import type { Conversation } from '@/types/chat'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { MessageCircle, Plus } from 'lucide-react'

import ConversationList from './ConversationList'

interface SidebarContentProps {
  conversations: Conversation[]
  isLoading: boolean
}

export default function SidebarContent({ conversations, isLoading }: SidebarContentProps) {
  const { conversationId } = useParams({ strict: false }) as { conversationId?: string }
  const { refetchConversations } = useConversationActions()
  const navigate = useNavigate()

  const handleDelete = async (id: string) => {
    await ConversationsApiService.destroy(id)
    refetchConversations()
    if (conversationId === id) {
      navigate({ to: '/app/chat' })
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-5 w-5" />
          <h2 className="font-semibold">Chats</h2>
        </div>
        <Button variant="ghost" size="icon" asChild>
          <Link to="/app/chat">
            <Plus className="h-4 w-4" />
          </Link>
        </Button>
      </div>
      <Separator />
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex flex-col gap-1 p-2">
          {isLoading ? (
            ['sk-1', 'sk-2', 'sk-3', 'sk-4', 'sk-5'].map((key) => (
              <div key={key} className="flex flex-col gap-2 rounded-lg px-3 py-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ))
          ) : (
            <ConversationList
              conversations={conversations}
              conversationId={conversationId}
              onDelete={handleDelete}
            />
          )}
        </div>
      </div>
    </div>
  )
}
